import React, { useContext } from 'react'
import { Route, Routes, BrowserRouter as Router, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { Layout, ConfigProvider, theme } from 'antd'
import { ChakraProvider } from '@chakra-ui/react'

import Navigation from './Components/Navigation/Navigation'
import ProtectedRoutes from './Routes/ProtectedRoutes'
import FooterContent from './Components/FooterContent'
import { ThemeContext } from './Theme/ThemeContext'
import { CourseContext, CourseProvider } from './Components/Course/CourseContext'
import LoginPage from './Routes/LoginPage'
import { EditorSettingsContext, EditorSettingsProvider } from './Components/Editor/EditorSettingsContext'
import { AuthContext } from './Components/Auth/AuthContext'
import { FileContext, FileProvider } from './Components/Course/FileContext'
import { UserContext, UserProvider } from './Components/User/UserContext'
import CourseForParticipants from './Routes/CourseForParticipants'
import { ParticipantCourseProvider } from './Components/CourseForParticipants/Context/ParticipantCourseContext'
import GetCertificate from './Components/GetCertificate/GetCertificate'
import VerifyCertificate from './Components/VerifyCertificate/VerifyCertificate'
import CourseCatalog from './Components/CourseCatalog/CourseCatalog'
import CourseFromCatalog from './Components/CourseCatalog/CourseFromCatalog'
import BuyCourseAccess from './Components/CourseCatalog/BuyCourseAccess/BuyCourseAccess'
import ExamResults from './Components/CourseForParticipants/Exam/ExamResults'
import Exam from './Components/CourseForParticipants/Exam/Exam'

const { Content } = Layout
const { defaultAlgorithm, darkAlgorithm } = theme

const App: React.FC = () => {
  const { examLink } = useParams<{ examLink: string }>()
  const { examAttemptId } = useParams<{ examAttemptId: string }>()

  const { isDarkMode } = useContext(ThemeContext)

  const { setSelectedFile } = useContext(FileContext)

  const {
    courseContext,
    setCourseContext,
    createCourse,
    deleteCourse,
    deleteCoursePage,
    updateCourse,
    saveCourse,
    publishCourse,
  } = useContext(CourseContext)

  const {
    editorSettingsContext,
    setEditorSettingsContext,
    updateEditorSettingsContext
  } = useContext(EditorSettingsContext)

  const { isAuthenticated } = useContext(AuthContext)

  const {
    userDetails,
    getUserDetails,
    changeProfilePicture,
    changeUserDetails
  } = useContext(UserContext)

  return (
    <ChakraProvider>
      <ConfigProvider theme={{
        algorithm: isDarkMode ? darkAlgorithm : defaultAlgorithm,
      }}>
        <Router>
          <Routes>
            <Route path="/course-catalog" element={<CourseCatalog />} />
            <Route path="/course-catalog/:courseId" element={<CourseFromCatalog />}/>
            <Route path="/buy-course-access/:courseId" element={<BuyCourseAccess />}/>
            <Route path="/exam/results/:examAttemptId" element={<ExamResults />} />
            <Route path="/exams/:examLink" element={(
              <ParticipantCourseProvider>
                <CourseForParticipants examLink={examLink}/>
              </ParticipantCourseProvider>
            )} />
            <Route path="/exams/:examLink/attempt/:examAttemptId" element={(
              <ParticipantCourseProvider>
                <Exam examAttemptId={examAttemptId}/>
              </ParticipantCourseProvider>
            )} />
            <Route path="/get-certificate/:examId" element={<GetCertificate />} />
            <Route path="/verify-certificate/:examId" element={<VerifyCertificate />} />
            <Route path="*" element={
              isAuthenticated 
                ? (
                  <FileProvider
                    selectedFile={null}
                    setSelectedFile={setSelectedFile}
                  >
                    <CourseProvider
                      courseContext={courseContext}
                      setCourseContext={setCourseContext}
                      createCourse={createCourse}
                      deleteCourse={deleteCourse}
                      deleteCoursePage={deleteCoursePage}
                      updateCourse={updateCourse}
                      saveCourse={saveCourse}
                      publishCourse={publishCourse}
                    >
                      <EditorSettingsProvider
                        editorSettingsContext={editorSettingsContext}
                        setEditorSettingsContext={setEditorSettingsContext}
                        updateEditorSettingsContext={updateEditorSettingsContext}
                      >
                        <UserProvider
                          userDetails={userDetails}
                          getUserDetails={getUserDetails}
                          changeProfilePicture={changeProfilePicture}
                          changeUserDetails={changeUserDetails}
                        >
                          <Layout style={{height: '100%'}}>
                            <Navigation />
                            <Layout>
                              <StyledContent
                                isDarkMode={isDarkMode}
                              >
                                <ProtectedRoutes />
                              </StyledContent>
                            </Layout>
                            <FooterContent isDarkMode={isDarkMode} />
                          </Layout>
                        </UserProvider>
                      </EditorSettingsProvider>
                    </CourseProvider>
                  </FileProvider>
                ) 
                : <LoginPage />
            } />
          </Routes>
        </Router>
      </ConfigProvider>
    </ChakraProvider>
  )
}

export default App

const StyledContent = styled(Content)<{isDarkMode: boolean}>`
  background-color: ${(props) => (props.isDarkMode ? "#1f1f1f" : "white")};
  color: ${(props) => (props.isDarkMode ? "white" : "black")};
`